import { useState } from 'react';
import { buildWorkspaceEnvironment } from '@/lib/desktop-shell/environment';
import { errorMessage } from '@/lib/error-message';
import {
  type NativeWorkspaceEnvironment,
  readNativeWorkspaceEnvironment,
  writeNativeWorkspaceEnvironment,
} from '@/lib/native-desktop';

const EMPTY_ENVIRONMENT: NativeWorkspaceEnvironment = {
  name: 'Default',
  setupScript: '',
  setupScripts: {},
  cleanupScript: '',
  env: {},
  actions: [],
};

type UseEnvironmentSettingsParams = {
  workspace: string | null;
  worktreeSourceRoot: string | null;
  workspaceEnvironment: NativeWorkspaceEnvironment | null;
  /** Writes the environment of a repository: the shell keeps it while that one is shown. */
  setWorkspaceEnvironment: (root: string, environment: NativeWorkspaceEnvironment | null) => void;
  preview: boolean;
};

/**
 * Owns the "Local environment" dialog state and its open/save flow. The active
 * workspace environment itself stays with the caller (it feeds effects, memos
 * and other UI), so it is passed in and written back through here.
 */
export function useEnvironmentSettings({
  workspace,
  worktreeSourceRoot,
  workspaceEnvironment,
  setWorkspaceEnvironment,
  preview,
}: UseEnvironmentSettingsParams) {
  const [environmentDialogOpen, setEnvironmentDialogOpen] = useState(false);
  const [environmentProjectRoot, setEnvironmentProjectRoot] = useState<string | null>(null);
  const [environmentDraft, setEnvironmentDraft] =
    useState<NativeWorkspaceEnvironment>(EMPTY_ENVIRONMENT);
  const [environmentEnvText, setEnvironmentEnvText] = useState('');
  const [environmentError, setEnvironmentError] = useState<string | null>(null);

  async function openEnvironmentSettings(rootOverride?: string): Promise<void> {
    const activeRoot = worktreeSourceRoot ?? workspace;
    const root = rootOverride ?? activeRoot;
    if (root === null) return;
    const configuredEnvironment =
      root === activeRoot
        ? workspaceEnvironment
        : await readNativeWorkspaceEnvironment(root).catch(() => null);
    const environment = configuredEnvironment ?? EMPTY_ENVIRONMENT;
    setEnvironmentDraft({
      ...environment,
      setupScripts: environment.setupScripts ?? {},
      actions: environment.actions ?? [],
    });
    setEnvironmentEnvText(
      Object.entries(environment.env)
        .map(([key, value]) => `${key}=${value}`)
        .join('\n'),
    );
    setEnvironmentError(null);
    setEnvironmentProjectRoot(root);
    setEnvironmentDialogOpen(true);
  }

  async function saveEnvironment(): Promise<void> {
    const root = environmentProjectRoot ?? worktreeSourceRoot ?? workspace;
    if (root === null || preview) return;
    const built = buildWorkspaceEnvironment(environmentDraft, environmentEnvText);
    if (!built.ok) {
      setEnvironmentError(built.error);
      return;
    }
    setEnvironmentError(null);
    try {
      await writeNativeWorkspaceEnvironment(root, built.environment);
      setWorkspaceEnvironment(root, built.environment);
      setEnvironmentDialogOpen(false);
    } catch (error) {
      setEnvironmentError(errorMessage(error));
    }
  }

  return {
    environmentDialogOpen,
    setEnvironmentDialogOpen,
    environmentProjectRoot,
    setEnvironmentProjectRoot,
    environmentDraft,
    setEnvironmentDraft,
    environmentEnvText,
    setEnvironmentEnvText,
    environmentError,
    setEnvironmentError,
    openEnvironmentSettings,
    saveEnvironment,
  };
}
